export const MAX_EQUATION_DATA_CHARS = 50000;
export const MAX_EQUATION_CHARS = 1000;

/**
 * Parse equations from response text_submitted_text field
 *
 * @param {Object|null} response - Response object with text_submitted_text
 * @returns {Array<string>} Array of equation strings
 */
export const parseEquationsFromResponse = (response) => {
  // Handle null/undefined response
  if (!response) {
    return [''];
  }

  try {
    let submittedText;
    if (response.get && typeof response.get === 'function') {
      // Immutable object
      submittedText = response.get('text_submitted_text');
    } else if (typeof response === 'object') {
      // Plain object
      submittedText = response.text_submitted_text;
    } else {
      throw new Error(
        'Invalid response format: expected object with text_submitted_text field'
      );
    }

    return parseEquationsFromText(submittedText);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error parsing equations from response:', error);
    throw new Error(`Response parsing failed: ${error.message}`);
  }
};

/**
 * Parse equations from raw text string
 *
 * @param {string|null|undefined} submittedText - Raw text to parse
 * @returns {Array<string>} Array of equation strings
 */
export const parseEquationsFromText = (submittedText) => {
  // Handle empty/null/undefined
  if (
    !submittedText ||
    (typeof submittedText === 'string' && submittedText.trim() === '')
  ) {
    return [''];
  }

  // Basic sanity check on text length
  if (
    typeof submittedText === 'string' &&
    submittedText.length > MAX_EQUATION_DATA_CHARS
  ) {
    throw new Error('Submitted text too large to parse safely');
  }

  try {
    const parsed = JSON.parse(submittedText);

    if (Array.isArray(parsed)) {
      const validatedEquations = parsed.map((equation, index) => {
        if (typeof equation !== 'string') {
          // eslint-disable-next-line no-console
          console.warn(
            `Equation ${index + 1} is not a string, converting:`,
            equation
          );
          return String(equation);
        }
        return equation;
      });

      return validatedEquations.length > 0 ? validatedEquations : [''];
    } else {
      console.warn('Expected array but got:', typeof parsed, parsed);
      return [String(parsed)];
    }
  } catch (error) {
    console.warn(
      'JSON parsing failed, treating as legacy text:',
      error.message
    );
    return [
      typeof submittedText === 'string' ? submittedText : String(submittedText),
    ];
  }
};

/**
 * Format equations for submission to backend
 *
 * @param {Array<string>} equations - Array of equation strings
 * @returns {string} JSON string for text_submitted_text field
 */
export const formatEquationsForSubmission = (equations) => {
  if (!Array.isArray(equations)) {
    throw new Error('formatEquationsForSubmission expects an array');
  }

  const safeEquations = equations.map((equation, index) => {
    if (typeof equation !== 'string') {
      // eslint-disable-next-line no-console
      console.warn(
        `Equation ${index + 1} is not a string, converting:`,
        equation
      );
      return String(equation || '');
    }
    return equation;
  });

  let cleanEquations = [...safeEquations];

  // Remove trailing empty equations
  while (
    cleanEquations.length > 1 &&
    cleanEquations[cleanEquations.length - 1].trim() === ''
  ) {
    cleanEquations.pop();
  }

  if (cleanEquations.length === 0) {
    cleanEquations = [''];
  }

  try {
    const result = JSON.stringify(cleanEquations);

    // Sanity check result size
    if (result.length > MAX_EQUATION_DATA_CHARS) {
      throw new Error(
        `Formatted equations too large for submission (max ${MAX_EQUATION_DATA_CHARS} chars)`
      );
    }

    return result;
  } catch (error) {
    throw new Error(`Failed to format equations: ${error.message}`);
  }
};

/**
 * Validate equation array for common issues
 *
 * @param {Array<string>} equations - Array of equation strings to validate
 * @returns {Object} Validation result with isValid and errors
 */
export const validateEquations = (equations) => {
  const errors = [];

  if (!Array.isArray(equations)) {
    errors.push('Equations must be an array');
    return { isValid: false, errors };
  }

  if (equations.length === 0) {
    errors.push('At least one equation is required');
  }

  // Check for overly long equations (LaTeX can get complex)
  equations.forEach((equation, index) => {
    if (typeof equation !== 'string') {
      errors.push(`Equation ${index + 1} must be a string`);
    } else if (equation.length > MAX_EQUATION_CHARS) {
      errors.push(
        `Equation ${
          index + 1
        } is too long (max ${MAX_EQUATION_CHARS} characters)`
      );
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Check if equations array represents an empty response
 *
 * @param {Array<string>} equations - Array of equation strings
 * @returns {boolean} True if response is effectively empty
 */
export const isEmptyResponse = (equations) => {
  if (!Array.isArray(equations) || equations.length === 0) {
    return true;
  }

  // Check if all equations are empty or whitespace-only
  return equations.every((equation) => !equation || equation.trim() === '');
};
